import { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { classNames as cx } from "primereact/utils";
import { Controller, UseFormReturn } from "react-hook-form";
import moment from "moment";

import { FormFieldsPanel } from "../../../../../components/form-fields-panel/form-fields-panel";
import { If } from "../../../../../components/If/If";
import { LabelValue } from "../../../../../components/LabelValue/LabelValue";
import {
  JOB_ASSIGNABLE_ROLES,
  JOB_SUPERVISOR_ROLES,
} from "../../../../../models/Employee";
import { requests } from "../../../../../services/http-common";
import {
  JobFrequencyEnum,
  TJobFormData,
  isDayPatternInUse,
} from "../CreateJobAndSchedule.model";
import {
  DayPatternEntry,
  describeInvalidPatternDays,
  syncPatternWithDays,
} from "../day-pattern";
import { DayPatternFields } from "./DayPatternFields";
import { GroupedConflict } from "../schedule-conflicts";
import { useAllEmployeeOptions } from "../../../../../hooks/useEmployeeOptions";

import classes from "./ScheduleForm.module.scss";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { AttachMultipleDocsPopup } from "../../../../../components/AttachMultipleDocsPopup/AttachMultipleDocsPopup";
import RecurrenceForm from "../../../../../components/RecurrenceForm/RecurrenceForm";

/**
 * The form holds the job's own times as `Date | null` (PrimeReact's Calendar
 * with showTime), while a pattern entry holds "HH:mm". `moment(null)` formats
 * to the literal string "Invalid date", which the backend's
 * /^([01]\d|2[0-3]):[0-5]\d$/ then rejects, so an absent or unparseable time
 * becomes "" instead - describeInvalidPatternDays already reports that as
 * "Add a start time." on the card the user can fix it on.
 */
const toPatternTime = (value: Date | string | null | undefined): string =>
  value && moment(value).isValid() ? moment(value).format("HH:mm") : "";

const lateInOptions = [
  { label: "5 min", value: 5 },
  { label: "10 min", value: 10 },
  { label: "30 min", value: 30 },
];

type ScheduleFormProps = {
  form: UseFormReturn<TJobFormData, any>;
  isScheduleEdit?: boolean;
  /**
   * Editing an existing SERIES (the job edit route), as opposed to creating one.
   * Distinct from `isScheduleEdit`, which is the per-occurrence page - the one
   * PER_OCCURRENCE_NOTE below sends people to.
   */
  isSeriesEdit?: boolean;
  /**
   * Whether `PUT /job/:id/series` can act on this job at all - which is to say,
   * whether the job as LOADED is a weekly recurrence. Only then can the fields
   * that belong to each occurrence be changed across the series; see
   * useJobAndScheduleForm's isSeriesEditable, which decides it, and note that
   * the form's own Frequency control is not what answers the question.
   */
  isSeriesEditable?: boolean;
  jobId?: number | undefined;
  /** Grouped clashes for the day cards; see DayPatternFields. */
  conflictsByWeekday?: Record<number, GroupedConflict[]>;
};

/**
 * Why a field is dead on the series edit route. These values belong to the
 * individual occurrences, not to the series: the form seeds them from the first
 * occurrence only so they can be shown, and `PUT /job/:id/series` has nowhere to
 * put them. Left enabled they took keystrokes and dropped them on save.
 *
 * Still the right words for a job the series route cannot touch - one that is
 * not a weekly recurrence - where every one of these fields really is only
 * editable a shift at a time.
 */
const PER_OCCURRENCE_NOTE =
  "Set per occurrence \u2014 open a shift on the calendar to change it.";

/**
 * Why the job's own hours are dead while the job runs on a per-day pattern.
 *
 * PER_OCCURRENCE_NOTE used to be shown here too and was actively wrong: the
 * hours ARE editable on this very screen, on the day cards below, and those
 * cards save across the whole series through `PUT /job/:id/series`. It sent the
 * user to the calendar instead of a few inches down the page.
 *
 * These two are not made series-editable the way Supervisors and the division
 * are, and that is a finding rather than an omission. Nothing reads a job-level
 * start or end time once a pattern is in use: `expandDayPattern` takes every
 * occurrence's hours from the pattern entry, its anchor from the series' first
 * existing occurrence and its window end from `jobEndTime` - which the Ends By
 * control already sends. The pair on screen is occurrence [0]'s own datetimes,
 * shown so the series has a readable start, and a job-level control over "the
 * hours" beside per-weekday cards is exactly the duplication that left every
 * day of the week looking identical.
 */
const DAY_CARD_NOTE =
  "Set per day \u2014 change it on the day cards below, which save across the series.";

export const ScheduleForm = ({
  form,
  isScheduleEdit,
  isSeriesEdit,
  isSeriesEditable,
  jobId,
  conflictsByWeekday,
}: ScheduleFormProps) => {
  const [, setEndDateCalendarDate] = useState<Date>(
    moment().add(30, "minutes").toDate()
  );

  const [pastDates, setPastDates] = useState<Date[]>([]);

  const [openMultipleDocsPopup, setOpenMultipleDocsPopup] =
    useState<boolean>(false);

  const { data: jobZonePayload } = useQuery({
    queryKey: ["job-zones"],
    queryFn: () => {
      return requests.get(`/job-zone`);
    },
  });

  const { options: allEmployeeOptions } = useAllEmployeeOptions();

  const employeeOptions = useMemo(
    () => allEmployeeOptions.filter((option: any) => JOB_ASSIGNABLE_ROLES.includes(option.role)),
    [allEmployeeOptions],
  );

  const supervisorOptions = useMemo(
    () => allEmployeeOptions.filter((option: any) => JOB_SUPERVISOR_ROLES.includes(option.role)),
    [allEmployeeOptions],
  );

  const zoneCodes = useMemo(() => {
    if (!jobZonePayload) return [];
    return jobZonePayload.result.map((option: any) => ({
      label: option.name,
      value: option.id,
    }));
  }, [jobZonePayload]);

  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = form;

  const [
    recurrence,
    daysOfWeek,
    dayPattern,
    frequency,
    startTime,
    endTime,
    attachments,
  ] = watch([
    "recurrence",
    "daysOfWeek",
    "dayPattern",
    "frequency",
    "startTime",
    "endTime",
    "attachments",
  ]);

  // The edit flow shares this form but has no pattern of its own yet, so it
  // keeps the job-level Employees field whatever the recurrence says.
  const patternInUse =
    !isScheduleEdit && isDayPatternInUse({ recurrence, frequency, dayPattern });

  /**
   * Whether the four fields that belong to each occurrence - the division, the
   * supervisors and the two alert times - can be changed for the whole series
   * from here.
   *
   * Both halves are load-bearing, and they fail differently:
   *
   *  - `isSeriesEditable` is the job as LOADED being a weekly recurrence.
   *    `updateDayPattern` refuses anything else, and the form's own Recurrence
   *    and Frequency controls do not change what the endpoint reads, so a
   *    monthly job flipped to Weekly on screen must not light these up.
   *  - `patternInUse` is there being a pattern to send. `dayPattern` is
   *    @ArrayNotEmpty on the series body and `saveSeries` refuses outright
   *    without one, so the four would otherwise take keystrokes on a weekly job
   *    whose weekdays have not been picked - and drop them on save, which is
   *    the behaviour this whole change exists to remove. Watched rather than
   *    loaded, so they come alive the moment a weekday is picked, which is
   *    exactly when the save can succeed.
   */
  const seriesFieldsEditable = Boolean(isSeriesEdit && isSeriesEditable && patternInUse);

  /**
   * Rendered under each disabled control rather than once for the panel: the
   * fields are scattered through the grid between editable ones, and a single
   * note at the top would not say which of them it governs.
   *
   * Nothing to say once a field is live, which is why this returns null for the
   * four the series route now carries.
   */
  const perOccurrenceNote = () =>
    isSeriesEdit ? <small className={classes.perOccurrence}>{PER_OCCURRENCE_NOTE}</small> : null;

  /**
   * The note for the four fields the series route now carries: nothing at all
   * once they are live, and the unchanged per-occurrence wording on a job the
   * endpoint will not accept, where it is still true.
   *
   * Kept apart from perOccurrenceNote - which the job-level Employees field
   * still uses - because the two differ on exactly one job: a weekly recurrence
   * with no pattern yet. There the division and the alert times ARE
   * series-editable while the job-level crew is not, so one helper for both
   * would drop the note from a field that is still dead.
   */
  const seriesFieldNote = () =>
    seriesFieldsEditable ? null : perOccurrenceNote();

  /**
   * The note for the job's own Start and End Time, which stay dead on the edit
   * route either way. Where to send the user is what differs: to the day cards
   * when a pattern is in use, because that is where the hours live and they
   * save across the series; to the calendar otherwise, because without a
   * pattern there are no day cards and the old wording is the true one.
   */
  const jobHoursNote = () =>
    isSeriesEdit ? (
      <small className={classes.perOccurrence}>
        {patternInUse ? DAY_CARD_NOTE : PER_OCCURRENCE_NOTE}
      </small>
    ) : null;

  const getFormErrorMessage = (name: any) => {
    return (
      // ✅ THIS IS OK
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const handleWeekDayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const daysSet = new Set(daysOfWeek);
    //@ts-ignore
    const dayValue = +e.target.getAttribute("data-day-index");
    if (daysSet.has(dayValue)) {
      daysSet.delete(dayValue);
    } else {
      daysSet.add(dayValue);
    }
    //@ts-ignore
    const daysArr: number[] = [...daysSet];
    setValue("daysOfWeek", [...daysArr]);
    // Synced from the click rather than an effect on daysOfWeek: an effect
    // would also fire on the lead query's reset() and on a draft restore, and
    // would dedupe a pattern loaded from the API - the very repeat
    // describeInvalidPatternDays exists to report.
    setValue(
      "dayPattern",
      syncPatternWithDays(
        getValues("dayPattern") ?? [],
        daysArr,
        toPatternTime(startTime),
        toPatternTime(endTime)
      )
    );
  };

  const handleAddOtherDocuments = (files: File[]) => {
    setValue(
      "attachments",
      attachments ? [...attachments, ...files] : [...files]
    );
  };

  const handleDeleteAttachment = async (path?: string) => {
    // Newly picked files are not on the job yet - nothing to delete server side.
    if (!path || !jobId) {
      return;
    }

    await requests.delete(`/job/${jobId}/attachment`, {
      data: { attachment2Path: [path] },
    });
  };

  const generatePastDatesArray = () => {
    const today = moment();
    const pastDatesArray: Date[] = [];

    for (let i = 1; i <= 365; i++) {
      pastDatesArray.push(today.clone().subtract(i, 'days').toDate());
    }

    return pastDatesArray;
  };

  useEffect(() => {
    setPastDates(generatePastDatesArray());
  }, []);


  return (
    <FormFieldsPanel
      label="Schedule Details"
      className={cx(classes.fieldsPanel)}
    >
      <div className={classes.gridTemplate}>
        <LabelValue label="Service Division" isRequired>
          <Controller
            name="zoneCode"
            render={({ field }) => (
              <Dropdown
                {...field}
                appendTo="self"
                options={zoneCodes}
                // The visible label is a plain div, not a <label>, so the
                // control carries its own name - as Employees already does.
                aria-label="Service Division"
                style={{ width: "100%" }}
                // On the component, never on the Controller: react-hook-form's
                // own `disabled` submits the field as undefined, which the
                // schema would then read as missing.
                //
                // Live on the edit route now, for a job the series endpoint
                // accepts: `PUT /job/:id/series` applies a new division to the
                // occurrences it regenerates - future and unencumbered ones
                // only, so a shift already worked keeps the division it had.
                disabled={!seriesFieldsEditable && isSeriesEdit}
                className={cx({ "p-invalid": errors.zoneCode })}
              />
            )}
            control={control}
          />
          {seriesFieldNote()}
          {getFormErrorMessage("zoneCode")}
        </LabelValue>
        {/*
          Hidden, not disabled, while the per-day pattern is in use: the pattern
          says who works each day, and a second job-wide crew beside it would
          only be a contradiction. Supervisors stays, since it is set per job in
          both modes.
        */}
        <If condition={!patternInUse}>
          <LabelValue label="Employees" isRequired>
            <Controller
              name="employeeIds"
              render={({ field }) => (
                <MultiSelect
                  {...field}
                  appendTo="self"
                  options={employeeOptions}
                  placeholder="Select employee"
                  // The visible "Employees" text is a plain div, not a <label>,
                  // so the control has to carry its own name.
                  aria-label="Employees"
                  style={{ width: "100%" }}
                  display="chip"
                  maxSelectedLabels={2}
                  disabled={isSeriesEdit}
                  className={cx({ "p-invalid": errors.employeeIds })}
                />
              )}
              control={control}
            />
            {perOccurrenceNote()}
            {getFormErrorMessage("employeeIds")}
          </LabelValue>
        </If>
        <LabelValue label="Supervisors">
          <Controller
            name="supervisorIds"
            render={({ field }) => (
              <MultiSelect
                {...field}
                appendTo="self"
                options={supervisorOptions}
                aria-label="Supervisors"
                placeholder="Service Division's supervisors"
                display="chip"
                maxSelectedLabels={2}
                style={{ width: "100%" }}
                // Series-wide on the edit route: the endpoint puts this set on
                // every occurrence it regenerates. Cleared, it falls back to
                // the Service Division's supervisors - which is what the
                // placeholder above promises and what the API does with an
                // empty list.
                disabled={!seriesFieldsEditable && isSeriesEdit}
              />
            )}
            control={control}
          />
          {seriesFieldNote()}
        </LabelValue>
        <LabelValue label="Job name" isRequired>
          <Controller
            name="name"
            render={({ field }) => (
              <InputText
                {...field}
                className={cx({ "p-invalid": errors.name })}
                style={{ width: "100%" }}
              />
            )}
            control={control}
          />
          {getFormErrorMessage("name")}
        </LabelValue>
        {/* <LabelValue label="Duration">
          <Controller
            name="clientFax"
            render={({ field }) => (
              <InputText {...field} disabled style={{ width: "100%" }} />
            )}
            control={control}
          />
        </LabelValue> */}
        <LabelValue label="Start Time" isRequired>
          <Controller
            name="startTime"
            control={control}
            render={({ field }) => (
              <Calendar
                id={field.name}
                value={moment(field.value).toDate() || new Date()}
                onChange={(e) => {
                  const endMinDate = moment(e.value as any)
                    .add(30, "minutes")
                    .toDate();

                  setEndDateCalendarDate(endMinDate);
                  setValue("endTime", endMinDate);
                  field.onChange(e.value);
                }}
                placeholder="MM/DD/YY"
                dateFormat="mm/dd/yy"
                showIcon
                hourFormat="12"
                showTime
                disabledDates={pastDates}
                disabled={isSeriesEdit}
                className={cx({ "p-invalid": errors.startTime })}
                style={{ width: "100%", marginLeft: 0 }}
              />
            )}
          />
          {jobHoursNote()}
          {getFormErrorMessage("startTime")}
        </LabelValue>
        <LabelValue label="End Time" isRequired>
          <Controller
            name="endTime"
            control={control}
            render={({ field }) => {
              return (
                <Calendar
                  id={field.name}
                  value={moment(field.value).toDate() || new Date()}
                  onChange={(e) => field.onChange(e.value)}
                  placeholder="MM/DD/YY"
                  dateFormat="mm/dd/yy"
                  showIcon
                  hourFormat="12"
                  showTime
                  disabledDates={pastDates}
                  disabled={isSeriesEdit}
                  className={cx({ "p-invalid": errors.endTime })}
                  style={{ width: "100%", marginLeft: 0 }}
                />
              );
            }}
          />
          {jobHoursNote()}
          {getFormErrorMessage("endTime")}
        </LabelValue>
        <LabelValue label="Duration">
          {endTime && startTime ? (
            <p>
              {moment
                .duration(moment(endTime).diff(moment(startTime)))
                .asHours()
                .toFixed()}{" "}
              Hours
            </p>
          ) : null}
        </LabelValue>
        <LabelValue
          className={classes.lateInAlerts}
          label="Late In Alerts"
          isRequired
        >
          <Controller
            name="lateInAlertTime"
            render={({ field }) => (
              <Dropdown
                {...field}
                appendTo="self"
                options={lateInOptions}
                aria-label="Late In Alerts"
                style={{ width: "100%" }}
                disabled={!seriesFieldsEditable && isSeriesEdit}
                className={cx({ "p-invalid": errors.lateInAlertTime })}
              />
            )}
            control={control}
          />
          {seriesFieldNote()}
          {getFormErrorMessage("lateInAlertTime")}
        </LabelValue>
        <LabelValue label="Late Out Alerts" isRequired>
          <Controller
            name="lateOutAlertTime"
            render={({ field }) => (
              <Dropdown
                {...field}
                appendTo="self"
                options={lateInOptions}
                aria-label="Late Out Alerts"
                style={{ width: "100%" }}
                disabled={!seriesFieldsEditable && isSeriesEdit}
                className={cx({ "p-invalid": errors.lateOutAlertTime })}
              />
            )}
            control={control}
          />
          {seriesFieldNote()}
          {getFormErrorMessage("lateOutAlertTime")}
        </LabelValue>
        <div></div>
        {isScheduleEdit && (
          <div className={classes.attachments}>
            <div className={classes.otherDocsTitle}>
              <span>Attachments</span>
              <Button
                label="Add files"
                icon="pi pi-plus"
                type="button"
                className="p-button-text"
                onClick={() => setOpenMultipleDocsPopup(true)}
              />
            </div>
            <div className={classes.docList}>
              <DataTable value={attachments || []}>
                <Column
                  header="Name"
                  body={(rowData) =>
                    rowData.path ? (
                      <a
                        href={`${process.env.REACT_APP_DSC_SERVICE_DOMAIN}/${rowData.path}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {rowData.name}
                      </a>
                    ) : (
                      <span>{rowData.name}</span>
                    )
                  }
                />
                <Column
                  style={{ width: "60px" }}
                  header="Actions"
                  body={(rowData) => (
                    <Button
                      icon="pi pi-times"
                      className=" p-button-sm p-button-rounded p-button-danger p-button-text"
                      aria-label="Cancel"
                      type="button"
                      onClick={() => {
                        const docsList = attachments.filter(
                          (_doc) => _doc.name !== rowData.name
                        );
                        setValue("attachments", docsList);
                        handleDeleteAttachment(rowData.path);
                      }}
                    />
                  )}
                />
              </DataTable>
            </div>
          </div>
        )}
        {!isScheduleEdit && (
          <LabelValue label="Notes">
            <Controller
              name="additionalNotes"
              render={({ field }) => (
                <InputTextarea
                  style={{ width: "100%" }}
                  autoResize
                  {...field}
                  rows={6}
                />
              )}
              control={control}
            />
          </LabelValue>
        )}
        <div></div>
        <div></div>
        {!isScheduleEdit && (
          <div className={classes.checkbox}>
            <Controller
              name="recurrence"
              control={control}
              render={({ field }) => (
                <Checkbox
                  inputId={field.name}
                  checked={field.value}
                  inputRef={field.ref}
                  onChange={(e) => field.onChange(e.checked)}
                />
              )}
            />
            <label htmlFor={"mobileAccess"}>Recurrence</label>
          </div>
        )}
      </div>
      {!isScheduleEdit && (
        <If condition={recurrence}>
          {/*
            One fragment, not two siblings: If renders children[0] when the
            condition holds and children[1] when it does not, so a second
            top-level child here would be read as the else branch.
          */}
          <>
            <RecurrenceForm
              control={control}
              daysOfWeek={daysOfWeek}
              errors={errors}
              frequency={frequency}
              getFormErrorMessage={getFormErrorMessage}
              handleWeekDayClick={handleWeekDayClick}
              recurrence={recurrence}
              startTime={startTime}
            />
            {/*
              Rendered here rather than inside RecurrenceForm: that component is
              shared with ContractForm and LeadForm, which have no per-day crew
              and no dayPattern field to write to. ScheduleForm already owns
              handleWeekDayClick, so the chip selection is right here anyway.
            */}
            <If condition={frequency === JobFrequencyEnum.WEEKLY}>
              <DayPatternFields
                value={(dayPattern ?? []) as DayPatternEntry[]}
                employeeOptions={employeeOptions}
                onChange={(next) => setValue("dayPattern", next)}
                conflictsByWeekday={conflictsByWeekday}
                // Computed from the very array being rendered: each message is
                // matched to its card by index.
                invalidDays={describeInvalidPatternDays(
                  (dayPattern ?? []) as DayPatternEntry[]
                )}
              />
            </If>
          </>
        </If>
      )}
      <If condition={openMultipleDocsPopup}>
        <AttachMultipleDocsPopup
          onValueSet={handleAddOtherDocuments}
          onHide={() => setOpenMultipleDocsPopup(false)}
        />
      </If>
    </FormFieldsPanel>
  );
};
